import WalletButton from '@/components/WalletButton';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { Factory, Home, ListChecks, Map, Recycle, ScanLine, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

// "My Activity" and "Profile" were merged into the Household Wallet page
// (impact summary + live balance + earnings history + editable profile), so
// they no longer need their own nav entries — /wallet covers all of it.
const navItems = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/household', label: 'Scan', icon: ScanLine },
  { to: '/my-listings', label: 'Listings', icon: ListChecks },
  { to: '/company', label: 'Company', icon: Factory },
  { to: '/map', label: 'Map', icon: Map },
  { to: '/wallet', label: 'Wallet', icon: Wallet },
];

/**
 * Sticky top nav (condenses on scroll) + a mobile bottom tab bar. Reused on
 * every page.
 */
export function Nav() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      {/* Top bar */}
      <motion.header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all duration-300',
          scrolled ? 'py-2' : 'py-4'
        )}
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className='container'>
          <div
            className={cn(
              'flex items-center justify-between rounded-full border px-4 py-2 transition-all duration-300',
              scrolled
                ? 'border-border/60 bg-card/70 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]'
                : 'border-transparent bg-transparent'
            )}
          >
            <NavLink to='/' className='flex items-center gap-2 shrink-0'>
              <span className='flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent'>
                <Recycle className='h-4 w-4 text-background' strokeWidth={2.5} />
              </span>
              <span className='font-heading text-lg font-bold tracking-tight text-foreground'>
                TrashLink
              </span>
            </NavLink>

            {/* Inline links — desktop only */}
            <nav className='hidden md:flex items-center gap-1'>
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <WalletButton />
          </div>
        </div>
      </motion.header>

      {/* Mobile bottom tab bar */}
      <nav className='fixed inset-x-0 bottom-0 z-50 border-t border-border/60 bg-card/80 backdrop-blur-xl md:hidden pb-[env(safe-area-inset-bottom)]'>
        <div className='grid grid-cols-6'>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-full transition-all',
                        isActive && 'bg-gradient-to-br from-primary/20 to-accent/20'
                      )}
                    >
                      <Icon className='h-4 w-4' strokeWidth={2.2} />
                    </span>
                    <span className='leading-none'>{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </>
  );
}

export default Nav;
